import { Component, inject, Inject, OnInit, ViewChild } from '@angular/core';
import { HierarchyListViewProviderDirective } from '@app/providers/hierarchy-list-view-provider';
import {
  AttributeStateService,
  CoreConfig,
  CoreConfigService,
  FilterViewProviderDirective,
  UIComponents
} from '@ntap/core';
import { PrimeNGTableComponent, TableMeta } from '@ntap/primeng';
import { TreeNode } from 'primeng/api';
import {
  TreeTable,
  TreeTableNodeCollapseEvent,
  TreeTableNodeExpandEvent,
  TreeTableNodeSelectEvent,
  TreeTableNodeUnSelectEvent
} from 'primeng/treetable';
import { combineLatest, first, map, Observable, startWith, tap } from 'rxjs';
import { AuthService } from '../login.component';

@Component({
  selector: 'app-hierarchy-list',
  templateUrl: './hierarchy-list.component.html',
  styleUrls: ['./hierarchy-list.component.css']
})
export class HierarchyListComponent
  extends PrimeNGTableComponent
  implements OnInit
{
  selected: TreeNode[];
  nodes$: Observable<TreeNode[]>;
  treeTableMeta$: Observable<TableMeta>;
  tableSelectionMode = 'single';

  filterCompId = UIComponents.FILTERVIEW;

  private authService = inject(AuthService);
  private filterViewProvider = inject(FilterViewProviderDirective);

  readonly defaultRowsPageOptions = [15, 25, 50, 100];

  rowsPerPageOptions$ = this.authService.validUser$.pipe(
    map((u) =>
      this.authService.userHasRole(u, 'Superuser')
        ? [...this.defaultRowsPageOptions, ...[1000]]
        : this.defaultRowsPageOptions
    ),
    startWith(this.defaultRowsPageOptions)
  );

  @ViewChild(TreeTable)
  tree: TreeTable;

  constructor(
    protected override listViewProvider: HierarchyListViewProviderDirective,
    @Inject(CoreConfigService) coreConfig: CoreConfig,
    attributeStateService: AttributeStateService
  ) {
    super(listViewProvider, coreConfig, attributeStateService);
    this.nodes$ = this.listViewProvider.treeViewData$;
    this.treeTableMeta$ = combineLatest([this.tableMeta$, this.view$]).pipe(
      map(([meta, view]) => {
        this.tableSelectionMode = meta.selectionMode;
        meta.columns = meta.columns.filter(
          (ci) => ci.attr.id !== (view as any)['hierarchy_column']
        );
        return meta;
      })
    );
  }

  ngOnInit() {
    if (this.filterViewProvider) {
      this.addSubscription(
        this.filterViewProvider.viewData$.subscribe((filterState) => {
          this.listViewProvider.viewState = {
            ...filterState,
            $parent: (this.listViewProvider.viewState as any)['$parent']
          };
        })
      );
    }
  }

  unsetSelectedItems(event: TreeTableNodeUnSelectEvent) {
    this.listViewProvider.selectedItems$
      .pipe(
        first(),
        tap((items) => {
          const deleted = items.filter((item) => item !== event.node?.data);
          super.setSelectedItems(deleted);
        })
      )
      .subscribe();
  }

  override setSelectedItems(event: TreeTableNodeSelectEvent): void {
    this.listViewProvider.selectedItems$
      .pipe(
        first(),
        tap((items) => {
          let inserted = <any>[];
          if (this.tableSelectionMode != 'single') {
            inserted = [...items];
          }
          inserted.splice(0, 0, event.node?.data);
          super.setSelectedItems(inserted);
        })
      )
      .subscribe();
  }

  nodeExpand(event: TreeTableNodeExpandEvent, nodes: TreeNode[]) {
    const node = event.node;
    this.listViewProvider
      .nodeExpand(node, nodes)
      .pipe(
        first(),
        tap(() => {
          this.tree.updateSerializedValue();
        })
      )
      .subscribe();
  }

  nodeCollapse(event: TreeTableNodeCollapseEvent) {
    this.listViewProvider.nodeCollapse(event.node);
    this.tree.updateSerializedValue();
  }
}
